import { useCallback, useEffect, useState } from 'react';

import { Deal } from '../../gql/dealGql';
import {
  MyUnfinishedThunderClaimsQuery,
  useMyUnfinishedThunderClaimsQuery,
} from '../../gql/generated/graphql';

import { DuplicateDealsModal } from '../DuplicateDealsModal/DuplicateDealsModal';

import ThunderInboundCallbackModal from './ThunderInboundCallbackModal';
import ThunderOutboundCallModal from './ThunderOutboundCallModal';

import { logger } from '../../libs/Logger';

type UnfinishedClaim = NonNullable<
  NonNullable<MyUnfinishedThunderClaimsQuery['myUnfinishedThunderClaims']>[number]
>;

export const THUNDER_MODAL_STORAGE_KEY = 'showThunderOutboundCallModal';
export const THUNDER_MODAL_DATA_STORAGE_KEY = 'thunderOutboundCallModalData';
export const THUNDER_MODAL_STORAGE_EVENT = 'thunderOutboundCallModalChange';

export type ThunderOutboundCallModalData = {
  scheduledTime?: string;
  phoneNumber: string | undefined;
  fullname: string;
  attemptNum: number;
  thunderCallNotificationId: number;
  aiCallOpener?: string;
  thunderPhoneNumber?: string;
  // Inbound callback: rep is already on the call via SIP answer, so no countdown.
  isCallback?: boolean;
  lastAttemptTimestamp?: string;
};

// All Thunder modal opens/closes go through these so storage + same-tab event stay in sync.
// (The `storage` event fires cross-tab only; the custom event covers same-tab listeners.)
export const writeThunderModalStorage = (leadData: ThunderOutboundCallModalData): void => {
  localStorage.setItem(THUNDER_MODAL_STORAGE_KEY, 'true');
  localStorage.setItem(THUNDER_MODAL_DATA_STORAGE_KEY, JSON.stringify(leadData));
  window.dispatchEvent(new Event(THUNDER_MODAL_STORAGE_EVENT));
};

export const clearThunderModalStorage = (): void => {
  localStorage.removeItem(THUNDER_MODAL_STORAGE_KEY);
  localStorage.removeItem(THUNDER_MODAL_DATA_STORAGE_KEY);
  window.dispatchEvent(new Event(THUNDER_MODAL_STORAGE_EVENT));
};

// One-shot recovery for orphaned claims (rep clicked ANSWER but the response was lost
// before localStorage was written). Caller is expected to flip `enabled` to false after
// the first response so Apollo never refetches.
type UseRecoverOrphanedThunderClaimArgs = {
  enabled: boolean;
  onRecover: (claim: UnfinishedClaim) => void;
  onDone: () => void;
};

const useRecoverOrphanedThunderClaim = ({
  enabled,
  onRecover,
  onDone,
}: UseRecoverOrphanedThunderClaimArgs): void => {
  useMyUnfinishedThunderClaimsQuery({
    fetchPolicy: 'network-only',
    skip: !enabled,
    onCompleted: (data) => {
      const claim = data?.myUnfinishedThunderClaims?.[0];
      if (claim != null && claim.id != null) {
        onRecover(claim);
      }
      onDone();
    },
    onError: (error) => {
      logger.error(
        'ThunderOutboundCallModalManager.tsx',
        'Failed to fetch unfinished thunder claims',
        null,
        error,
      );
      onDone();
    },
  });
};

const ThunderOutboundCallModalManager = () => {
  const [showThunderOutboundCallModal, setShowThunderOutboundCallModal] = useState(() => {
    const stored = localStorage.getItem(THUNDER_MODAL_STORAGE_KEY);
    return stored === 'true';
  });

  const [modalData, setModalData] = useState<ThunderOutboundCallModalData | null>(() => {
    const stored = localStorage.getItem(THUNDER_MODAL_DATA_STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });

  // State for duplicate deals modal - lifted from ThunderOutboundCallModal
  // so it persists after the Thunder modal closes
  const [duplicateDealsModalOpen, setDuplicateDealsModalOpen] = useState(false);
  const [duplicateDealsData, setDuplicateDealsData] = useState<Deal[]>([]);

  // Only run recovery if no modal activity is in flight at mount.
  const [shouldRunRecovery, setShouldRunRecovery] = useState(
    () => !showThunderOutboundCallModal && modalData == null,
  );

  useRecoverOrphanedThunderClaim({
    enabled: shouldRunRecovery,
    onRecover: (claim) => {
      const leadData: ThunderOutboundCallModalData = {
        phoneNumber: claim.customer_phone_number ?? undefined,
        fullname: `${claim.first_name ?? ''} ${claim.last_name ?? ''}`.trim(),
        scheduledTime: claim.scheduled_call_time ?? undefined,
        attemptNum: claim.attempt_num ?? 1,
        thunderCallNotificationId: claim.id as number,
        aiCallOpener: claim.ai_call_opener ?? undefined,
        thunderPhoneNumber: claim.thunder_phone_number ?? undefined,
      };

      writeThunderModalStorage(leadData);
      setShowThunderOutboundCallModal(true);
      setModalData(leadData);
    },
    onDone: () => setShouldRunRecovery(false),
  });

  const handleDuplicatesFound = useCallback((deals: Deal[]) => {
    setDuplicateDealsData(deals);
    setDuplicateDealsModalOpen(true);
  }, []);

  const handleDuplicateDealsModalClose = useCallback(() => {
    setDuplicateDealsModalOpen(false);
    setDuplicateDealsData([]);
  }, []);

  useEffect(() => {
    // Listen for storage events to sync across tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THUNDER_MODAL_STORAGE_KEY) {
        setShowThunderOutboundCallModal(e.newValue === 'true');
      }
      if (e.key === THUNDER_MODAL_DATA_STORAGE_KEY) {
        if (e.newValue) {
          try {
            setModalData(JSON.parse(e.newValue));
          } catch {
            setModalData(null);
          }
        } else {
          setModalData(null);
        }
      }
    };

    // Listen for custom events for same-tab updates
    const handleCustomEvent = () => {
      const stored = localStorage.getItem(THUNDER_MODAL_STORAGE_KEY);
      setShowThunderOutboundCallModal(stored === 'true');

      const dataStored = localStorage.getItem(THUNDER_MODAL_DATA_STORAGE_KEY);
      if (dataStored) {
        try {
          setModalData(JSON.parse(dataStored));
        } catch {
          setModalData(null);
        }
      } else {
        setModalData(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(THUNDER_MODAL_STORAGE_EVENT, handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(THUNDER_MODAL_STORAGE_EVENT, handleCustomEvent);
    };
  }, []);

  const handleCloseModal = () => {
    setShowThunderOutboundCallModal(false);
    setModalData(null);
    clearThunderModalStorage();
  };

  // Both modals are always mounted and toggled via `isOpen` so each can rely on its
  // own `useEffect([isOpen])` to clean up session state — conditionally rendering would
  // unmount before isOpen=false fires and leave stale localStorage on the next open.
  // canShowModal gates until both storage keys land (writes happen in two calls, which
  // can fire two cross-tab events).
  const canShowModal = showThunderOutboundCallModal && modalData != null;
  const isCallback = !!modalData?.isCallback;

  return (
    <>
      <ThunderInboundCallbackModal
        isOpen={canShowModal && isCallback}
        onClose={handleCloseModal}
        leadData={modalData}
        onDuplicatesFound={handleDuplicatesFound}
      />
      <ThunderOutboundCallModal
        isOpen={canShowModal && !isCallback}
        onClose={handleCloseModal}
        leadData={modalData}
        onDuplicatesFound={handleDuplicatesFound}
      />

      <DuplicateDealsModal
        isOpen={duplicateDealsModalOpen}
        onClose={handleDuplicateDealsModalClose}
        duplicateDeals={duplicateDealsData}
        customMessage="This Thunder/Alex lead matches one or more deals already in the system. Please see below and send to your manager for review."
        allowDuplicateCreation={false}
      />
    </>
  );
};

export default ThunderOutboundCallModalManager;
